import type { ProductOwnerActivityDay } from '../stores/product_owner_runs'

// Pure grid-building logic for the Activity tab's GitHub-contribution-style
// heatmap (#1036), split out of the Vue component so the date-math is
// unit-testable without mounting anything. `today` is injectable (same
// pattern as useFormattedDate's `now` param) so tests stay deterministic.

// Kept in sync by hand with LOOKBACK in
// app/services/agent/product_owner_activity.rb — change one, check the
// other, since a JS constant can't be shared across the API boundary.
export const WEEKS = 52
const DAYS_PER_WEEK = 7

// The product-owner solution's first official date (#1062) — no genuine
// activity exists before this, so the grid never renders weeks earlier
// than it even when WEEKS would otherwise reach further back.
export const FIRST_DATE = new Date(2026, 6, 19)

export interface HeatmapCell {
  date: string
  totalRuns: number
  // null means "no runs that day" — distinct from 0, which would wrongly
  // read as "fully productive" on the color scale.
  idlePercentage: number | null
}

export interface HeatmapWeek {
  cells: (HeatmapCell | null)[]
}

function toDateOnly(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function isoDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

// Builds WEEKS-ish columns of 7 day-cells each, ending on `today`. The
// first column is backed up to the preceding Sunday so every column is a
// real calendar week; cells after `today` in the final (partial) week are
// null padding, same as GitHub's own heatmap.
export function buildHeatmapWeeks(days: ProductOwnerActivityDay[], today: Date = new Date()): HeatmapWeek[] {
  const byDate = new Map(days.map((d) => [d.date, d]))
  const end = toDateOnly(today)
  const start = new Date(end)
  start.setDate(start.getDate() - (WEEKS * DAYS_PER_WEEK - 1))
  start.setDate(start.getDate() - start.getDay())
  if (start < FIRST_DATE) start.setTime(FIRST_DATE.getTime())

  const weeks: HeatmapWeek[] = []
  const cursor = new Date(start)
  // eslint-disable-next-line no-unmodified-loop-condition -- the inner loop advances cursor via setDate
  while (cursor <= end) {
    const cells: (HeatmapCell | null)[] = []
    for (let i = 0; i < DAYS_PER_WEEK; i++) {
      if (cursor > end) {
        cells.push(null)
      } else {
        const iso = isoDate(cursor)
        const match = byDate.get(iso)
        cells.push({
          date: iso,
          totalRuns: match?.total_runs ?? 0,
          idlePercentage: match ? match.idle_percentage : null,
        })
      }
      cursor.setDate(cursor.getDate() + 1)
    }
    weeks.push({ cells })
  }
  return weeks
}

// Lower idle% is better — a fully-idle day reads as a warm color, a fully
// productive day as a strong green, no-data as neutral. Thresholds are
// coarse buckets (not gradients) so the grid stays scannable at a glance.
// Single source of truth for both the cell color (below) and the legend
// (ProductOwnerActivityHeatmap.vue) — highest threshold first.
export const HEATMAP_LEVELS = [
  { minIdlePercentage: 75, colorClass: 'bg-red-500/40', label: '75%+ idle' },
  { minIdlePercentage: 50, colorClass: 'bg-amber-500/40', label: '50–75% idle' },
  { minIdlePercentage: 25, colorClass: 'bg-emerald-500/30', label: '25–50% idle' },
  { minIdlePercentage: 0, colorClass: 'bg-emerald-500/70', label: '<25% idle' },
] as const

export const HEATMAP_NO_DATA_LEVEL = { colorClass: 'bg-white/5', label: 'No runs' } as const

export function heatmapCellColorClass(cell: HeatmapCell | null): string {
  if (!cell || cell.idlePercentage === null) return HEATMAP_NO_DATA_LEVEL.colorClass
  const level = HEATMAP_LEVELS.find((l) => cell.idlePercentage! >= l.minIdlePercentage)
  return level?.colorClass ?? HEATMAP_NO_DATA_LEVEL.colorClass
}

export function heatmapCellTitle(cell: HeatmapCell | null): string {
  if (!cell) return ''
  if (cell.idlePercentage === null) return `${cell.date}: no runs`
  const runLabel = cell.totalRuns === 1 ? 'run' : 'runs'
  return `${cell.date}: ${cell.totalRuns} ${runLabel}, ${cell.idlePercentage}% idle`
}
